<template>
  <div class="motorista-app" :class="{ 'motorista-app--sem-jornada': !inicioJornadaObrigatorio }">
    <router-view />

    <v-bottom-navigation
      v-if="inicioJornadaObrigatorio"
      v-model="currentView"
      fixed
      color="primary"
      grow
      app
    >
      <v-btn value="jornada" @click="navigateToJornada">
        <span>{{ $t('guiasMotorista.tabJornada') }}</span>
        <v-icon>mdi-clock-outline</v-icon>
      </v-btn>

      <v-btn value="guias" @click="navigateToGuias">
        <span>{{ $t('guiasMotorista.tabGuias') }}</span>
        <v-icon>mdi-format-list-bulleted</v-icon>
      </v-btn>
    </v-bottom-navigation>
  </div>
</template>

<script>
import axios from 'axios';
import { ApiBaseUrl } from '@/constants';
import EventBus from '@/utils/eventBus';
import { mapGetters } from 'vuex';

export default {
  name: 'MotoristaApp',
  provide() {
    return {
      motoristaJornadaState: this,
    };
  },
  data() {
    return {
      currentView: 'jornada',
      jornadaAtiva: null,
      token: null,
      idFuncionario: null,
      configLoaded: false,
      initialRouteSynced: false,
    };
  },
  computed: {
    ...mapGetters('config', ['motoristaInicioJornadaObrigatorio']),
    inicioJornadaObrigatorio() {
      if (!this.configLoaded) return true;
      return this.motoristaInicioJornadaObrigatorio;
    },
    jornadaEfetivaAtiva() {
      return Boolean(this.jornadaAtiva) || !this.inicioJornadaObrigatorio;
    },
  },
  watch: {
    '$route.name': {
      immediate: true,
      handler(name) {
        if (name === 'GuiasMotorista') {
          this.currentView = 'guias';
        } else if (name === 'JornadaMotorista') {
          this.currentView = 'jornada';
        }
        this.redirectIfJornadaDesabilitada();
        this.loadJornadaAtiva();
      },
    },
    inicioJornadaObrigatorio(required) {
      if (!required) {
        this.redirectIfJornadaDesabilitada();
      }
      this.publishJornadaState();
    },
  },
  created() {
    const raw = sessionStorage.getItem('user');
    if (raw) {
      try {
        const u = JSON.parse(raw);
        this.idFuncionario = u.id_funcionario != null ? u.id_funcionario : null;
      } catch (e) {
        this.idFuncionario = null;
      }
    }
    this.token = sessionStorage.getItem('api');
    EventBus.$on('motorista-jornada-updated', this.loadJornadaAtiva);
    this.initializeMotoristaApp();
  },
  beforeDestroy() {
    EventBus.$off('motorista-jornada-updated', this.loadJornadaAtiva);
  },
  methods: {
    async initializeMotoristaApp() {
      await this.ensureConfig();
      await this.loadJornadaAtivaAsync();
      this.syncTelaInicial();
      this.publishJornadaState();
    },
    async ensureConfig() {
      const cfg = this.$store && this.$store.getters ? this.$store.getters['config/allConfig'] : null;
      const cfgCompleta =
        cfg &&
        cfg.motorista_inicio_jornada_obrigatorio != null &&
        cfg.motorista_checkout_minutos_apos_checkin != null;
      if (cfgCompleta) {
        this.configLoaded = true;
        return;
      }
      if (!this.token) {
        this.configLoaded = true;
        return;
      }
      try {
        const res = await axios.get(`${ApiBaseUrl}/api/v1.0/getallconfig`, {
          headers: { 'x-access-token': this.token },
        });
        const row = res && res.data ? res.data[0] : null;
        if (row) this.$store.commit('config/setConfig', row);
      } catch (e) {
        // mantém padrão (jornada obrigatória)
      } finally {
        this.configLoaded = true;
      }
    },
    redirectIfJornadaDesabilitada() {
      if (!this.configLoaded || this.inicioJornadaObrigatorio) return;
      if (this.$route.name === 'JornadaMotorista') {
        this.$router.replace({ path: '/guiasmotorista/guias' });
      }
    },
    syncTelaInicial() {
      if (!this.configLoaded || this.initialRouteSynced) return;
      this.initialRouteSynced = true;

      if (!this.inicioJornadaObrigatorio) {
        this.redirectIfJornadaDesabilitada();
        return;
      }

      if (this.$route.name === 'GuiasMotorista' && !this.jornadaAtiva) {
        this.$router.replace({ path: '/guiasmotorista' });
      }
    },
    loadJornadaAtiva() {
      this.loadJornadaAtivaAsync();
    },
    loadJornadaAtivaAsync() {
      if (!this.inicioJornadaObrigatorio) {
        this.jornadaAtiva = null;
        this.publishJornadaState();
        return Promise.resolve();
      }
      if (!this.token || !this.idFuncionario) {
        this.jornadaAtiva = null;
        this.publishJornadaState();
        return Promise.resolve();
      }
      return axios
        .get(`${ApiBaseUrl}/api/v1.0/getjornadamotoristaativa`, {
          headers: { 'x-access-token': this.token },
        })
        .then((res) => {
          const payload = res && res.data ? res.data : {};
          this.jornadaAtiva = payload.jornada || null;
          this.publishJornadaState();
        })
        .catch(() => {
          this.jornadaAtiva = null;
          this.publishJornadaState();
        });
    },
    publishJornadaState() {
      EventBus.$emit('motorista-jornada-state', this.jornadaEfetivaAtiva);
    },
    navigateToJornada() {
      this.currentView = 'jornada';
      if (this.$route.name !== 'JornadaMotorista') {
        this.$router.push({ path: '/guiasmotorista' });
      }
    },
    navigateToGuias() {
      this.currentView = 'guias';
      if (this.$route.name !== 'GuiasMotorista') {
        this.$router.push({ path: '/guiasmotorista/guias' });
      }
    },
  },
};
</script>

<style scoped>
.motorista-app :deep(.motorista-screen) {
  padding-bottom: 56px;
}

.motorista-app--sem-jornada :deep(.motorista-screen) {
  padding-bottom: 0;
}
</style>
